import { useLazyQuery } from '@apollo/client';
import { IconButton, Tooltip } from '@material-ui/core';
import { Send } from '@material-ui/icons';
import React from 'react';

import { hasJobPermission, JobRecruiterPermissions, JobStatus } from 'shared/models/job';

import { JOB_FOR_OUTREACH } from '../graphql/queries/outreach';
import { useModal } from '../hooks/use-modal';
import { useSession } from '../hooks/use-session';
import { OutreachSendDialogContainer } from './outreach-send-dialog-container';

export const JobOutreachScheduleNowButton: React.FC<{
    jobId: string;
    jobStatus?: JobStatus;
    iconColor?: string;
    iconSize?: 'small' | 'default';
}> = ({ jobId, jobStatus, iconColor, iconSize }) => {
    const { user } = useSession();
    const [fetchJob, { loading }] = useLazyQuery<
        {
            job: {
                id: string;
                status: JobStatus;
                dailyEmailLimit: number;
                recruiterPermissions: JobRecruiterPermissions;
                client: { name: string };
            };
        },
        { jobId: string }
    >(JOB_FOR_OUTREACH);

    const { hideLoading, setAlert, showLoading } = useModal();

    const [open, setOpen] = React.useState(false);
    const [job, setJob] = React.useState<{
        id: string;
        status: JobStatus;
        dailyEmailLimit: number;
        recruiterPermissions: JobRecruiterPermissions;
        client: { name: string };
    }>(null);
    const buttonSize = iconSize === 'small' ? 'small' : 'medium';

    const preDisabled = jobStatus !== undefined && jobStatus !== JobStatus.Active;

    const handleClick = async (event: React.SyntheticEvent<{}>) => {
        event.preventDefault();
        if (preDisabled) return;
        if (loading) return;

        try {
            showLoading();
            const result = await fetchJob({ fetchPolicy: 'cache-first', variables: { jobId } });
            const fetchedJob = result.data?.job;
            if (!fetchedJob) {
                setAlert('Error', 'Job not found');
                return;
            }

            const hasSendOutreachPermission = fetchedJob.recruiterPermissions?.sendOutreach
                ? hasJobPermission(fetchedJob.recruiterPermissions.sendOutreach, user.id)
                : false;
            const disallowed = !hasSendOutreachPermission;

            const blockedMessage = disallowed
                ? 'Job outreach disallowed'
                : fetchedJob.dailyEmailLimit <= 0
                  ? 'Outreach set to zero'
                  : fetchedJob.status === JobStatus.Active
                    ? null
                    : 'Job Not Active';

            if (blockedMessage) {
                setAlert('Cannot send outreach', blockedMessage);
                return;
            }

            setJob(fetchedJob);
            setOpen(true);
        } catch (e) {
            setAlert('Error', 'Error loading outreach');
        } finally {
            hideLoading();
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const dialog =
        open && job ? (
            <OutreachSendDialogContainer
                open={open}
                onClose={handleClose}
                jobId={jobId}
                client={{ name: job.client.name }}
                dailyEmailLimit={job.dailyEmailLimit}
            />
        ) : null;

    return (
        <>
            <Tooltip title={loading ? 'Loading...' : preDisabled ? 'Job Not Active' : 'Send Now'}>
                <span>
                    <IconButton size={buttonSize} onClick={handleClick} disabled={loading || preDisabled}>
                        <Send htmlColor={iconColor} fontSize={iconSize} />
                    </IconButton>
                </span>
            </Tooltip>
            {dialog}
        </>
    );
};
